import React from "react";

import {
  PrimaryEndpointTag,
  TableContainer,
  TableRowContainer,
  TableShellTitle,
  TableShellValue,
} from "@frontend/common";
import SwitchButton from "@frontend/common/src/shared/components/core/TableSwitchButton/TableSwitchButton";

import { Table } from "@EditArticleContainer/shared/components/ArticleTable/ArticleTable";
import EditableTextContainer from "@EditArticleContainer/shared/components/EditableTextContainer/EditableTextContainer";
import { theme } from "@styles/theme";

import * as S from "./ClinicalEndpointsTableSwitched.style";
import { prunedArticleTable } from "./utils/prunedArticleTable";
import { ClinicalEndpointsTable } from "../../../../utils/clinicalEndpointsTable";

interface Props {
  clinicalEndpointsTable: ClinicalEndpointsTable;
  tableIndex: number;
  switchTable: () => void;
}

const COMMON_EDITABLE_TEXT_STYLE = {
  padding: "0.8rem",
  borderRadius: "0",
  fontSize: "1.6rem",
  fontWeight: "600",
  lineHeight: "150%",
};

const COMMON_EDITABLE_TEXT_STYLE_TITLE = {
  ...COMMON_EDITABLE_TEXT_STYLE,
};

const PRIMARY_EDITABLE_TEXT_STYLE_TITLE = {
  ...COMMON_EDITABLE_TEXT_STYLE_TITLE,
  padding: "1.4rem 0.8rem 0.8rem",
};

const VALUE_SHELL_PADDING = "0.2rem";

const TABLE_SHELL_PADDING = {
  padding: "0.8rem",
  borderRadius: "0",
  fontSize: "1.6rem",
  fontWeight: "600",
  lineHeight: "150%",
};

const TABLE_SHELL_PADDING_PRIMARY_ENDPOINT = {
  ...TABLE_SHELL_PADDING,
  padding: "1.4rem 0.8rem",
};

const NOTE_SHELL_PADDING = {
  padding: "1rem",
  borderRadius: "0",
  fontSize: "1.6rem",
  fontWeight: "600",
  lineHeight: "150%",
};

const headerStyle = {
  background: theme.color.neural.gray02,
  fontWeight: 600,
};

const primaryEndpointTitleStyle = {
  background:
    "linear-gradient(0deg, rgba(11, 108, 255, 0.08) 0%, rgba(11, 108, 255, 0.08) 100%), #FFF",
  fontWeight: 600,
  padding: "2.4rem 1.6rem",
};

const normalEndpointTitleStyle = {
  background:
    "linear-gradient(0deg, rgba(11, 108, 255, 0.08) 0%, rgba(11, 108, 255, 0.08) 100%), #FFF",
  fontWeight: 600,
  padding: "2.4rem 1.6rem",
};

const primaryEndpointBodyStyle = {
  background:
    "linear-gradient(0deg, rgba(11, 108, 255, 0.04) 0%, rgba(11, 108, 255, 0.04) 100%), #FFF;",
  color: "#004AEB",
  fontWeight: 600,
};

const normalEndpointBodyStyle = {};

const ClinicalEndpointsTableSwitched = ({
  clinicalEndpointsTable,
  tableIndex,
  switchTable,
}: Props) => {
  const groupNameList = clinicalEndpointsTable.groupNames;

  const groupNameTitleItem = {
    refKey: `[Title] TC005-Dummy-${tableIndex}`,
    value: "Endpoints /\nGroup Name",
  };

  const groupNameItemList = clinicalEndpointsTable.groupNames.map(
    ({ text, labels, key }) => ({
      value: text,
      labels,
      canTextEdit: true,
      canLabelEdit: true,
      refKey: key,
    }),
  );

  const groupValuesList = clinicalEndpointsTable.groupValues.map((row) =>
    row.map(({ text, labels, key }) => ({
      value: text,
      labels,
      canTextEdit: true,
      canLabelEdit: true,
      refKey: key,
    })),
  );

  const rawTableItems: Table["tableItems"] = [
    [groupNameTitleItem, ...groupNameItemList],
    ...groupValuesList,
  ];

  const groupNameStyleList = rawTableItems[0].map(() => headerStyle);
  const groupValuesStyleList =
    clinicalEndpointsTable.endpointClassifications.map(({ isPrimary }) => [
      isPrimary ? primaryEndpointTitleStyle : normalEndpointTitleStyle,
      ...groupNameList.map(() =>
        isPrimary ? primaryEndpointBodyStyle : normalEndpointBodyStyle,
      ),
    ]);

  const checkPrimaryEndpointList =
    clinicalEndpointsTable.endpointClassifications.map(({ isPrimary }) =>
      [isPrimary, ...groupNameList.map(() => isPrimary)].every(
        (item) => item === true,
      ),
    );

  const endpointClassificationList =
    clinicalEndpointsTable.endpointClassifications;

  const rawStyles: Table["styles"] = [
    [...groupNameStyleList],
    ...groupValuesStyleList,
  ];

  const groupNameTitleComponent = <></>;
  const endpointClassificationTitleComponentList =
    clinicalEndpointsTable.endpointClassifications.map(({ isPrimary }) => [
      isPrimary ? (
        <S.PrimaryEndpointTag>Primary Endpoint</S.PrimaryEndpointTag>
      ) : (
        <></>
      ),
    ]);

  const rawComponents: Table["components"] = [
    [groupNameTitleComponent],
    ...endpointClassificationTitleComponentList,
  ];

  const tableNoteItem =
    clinicalEndpointsTable.notes.text === ""
      ? []
      : [
          {
            value: clinicalEndpointsTable.notes.text,
            canLabelEdit: true,
            canTextEdit: true,
            labels: clinicalEndpointsTable.notes.labels,
            refKey: clinicalEndpointsTable.notes.key,
          },
        ];

  const [tableItems] = prunedArticleTable(
    rawTableItems,
    rawStyles,
    rawComponents,
  );

  const transformData = () => {
    const newData = [];
    for (let i = 0; i < tableItems[0].length; i++) {
      const transformedList = tableItems.map((inner) => inner[i]);
      newData.push(transformedList);
    }
    return newData;
  };

  const switchTableItems = transformData();

  const checkIsTitle = (targetKey: string) => {
    return targetKey.includes("[Title]");
  };

  return (
    <S.TableWrapper>
      <S.TableTitle $isDisplay={!!(clinicalEndpointsTable.title.text !== "")}>
        <EditableTextContainer
          item={{
            text: clinicalEndpointsTable.title.text,
            labels: clinicalEndpointsTable.title.labels,
          }}
          refKey={clinicalEndpointsTable.title.key}
          canTextEdit
          data-switch="true"
        />
      </S.TableTitle>

      <TableContainer>
        {switchTableItems.map((row, rowIndex) => {
          if (rowIndex === 0) {
            return (
              <S.RowWrapper>
                <TableRowContainer>
                  {row.map((shell, shellIndex) => {
                    const isTitle = checkIsTitle(shell.refKey);
                    if (isTitle) {
                      return (
                        <TableShellTitle
                          key={shell.refKey}
                          padding={VALUE_SHELL_PADDING}
                        >
                          <S.ShellTitleWrapper className="endpoint-title">
                            <SwitchButton onClick={switchTable} />
                            {shell.value}
                          </S.ShellTitleWrapper>
                        </TableShellTitle>
                      );
                    }
                    return (
                      <TableShellTitle
                        padding={VALUE_SHELL_PADDING}
                        key={shell.refKey}
                        className={
                          checkPrimaryEndpointList[shellIndex - 1]
                            ? "is-primary-title-true"
                            : ""
                        }
                        style={{
                          position: "relative",
                        }}
                        id={`is-primary-shell-${!!checkPrimaryEndpointList[
                          shellIndex - 1
                        ]}`}
                      >
                        {!!checkPrimaryEndpointList[shellIndex - 1] && (
                          <PrimaryEndpointTag
                            primaryEndpointText={
                              endpointClassificationList[shellIndex - 1].text
                            }
                            isSwitchTable
                          />
                        )}
                        <EditableTextContainer
                          item={{
                            text: shell.value,
                            labels: shell.labels ?? [],
                          }}
                          canTextEdit
                          canLabelEdit
                          refKey={shell.refKey}
                          style={
                            checkPrimaryEndpointList[shellIndex - 1]
                              ? PRIMARY_EDITABLE_TEXT_STYLE_TITLE
                              : COMMON_EDITABLE_TEXT_STYLE_TITLE
                          }
                        />
                      </TableShellTitle>
                    );
                  })}
                </TableRowContainer>
              </S.RowWrapper>
            );
          }
          return (
            <S.RowWrapper>
              <TableRowContainer>
                {row.map((shell, shellIndex) => {
                  if (shellIndex === 0) {
                    return (
                      <>
                        <TableShellTitle key={shell.refKey} padding="0.1rem">
                          <EditableTextContainer
                            item={{
                              text: shell.value,
                              labels: shell.labels ?? [],
                            }}
                            canTextEdit
                            canLabelEdit
                            refKey={shell.refKey}
                            style={
                              checkPrimaryEndpointList[shellIndex - 1]
                                ? TABLE_SHELL_PADDING_PRIMARY_ENDPOINT
                                : TABLE_SHELL_PADDING
                            }
                          />
                        </TableShellTitle>
                      </>
                    );
                  }
                  return (
                    <TableShellValue
                      key={shell.refKey}
                      padding="0.1rem"
                      className={`is-primary-value-${!!checkPrimaryEndpointList[
                        shellIndex - 1
                      ]} is-last-row-${
                        rowIndex === switchTableItems.length - 1
                      }`}
                    >
                      <EditableTextContainer
                        item={{
                          text: shell.value,
                          labels: shell.labels ?? [],
                        }}
                        canTextEdit
                        canLabelEdit
                        refKey={shell.refKey}
                        style={TABLE_SHELL_PADDING}
                      />
                    </TableShellValue>
                  );
                })}
              </TableRowContainer>
            </S.RowWrapper>
          );
        })}
        {tableNoteItem.length > 0 && (
          <TableRowContainer>
            <TableShellValue padding="0.2rem" fontSize="1.4rem">
              <EditableTextContainer
                item={{
                  text: tableNoteItem[0].value,
                  labels: tableNoteItem[0].labels ?? [],
                }}
                canTextEdit
                canLabelEdit
                refKey={tableNoteItem[0].refKey}
                style={NOTE_SHELL_PADDING}
              />
            </TableShellValue>
          </TableRowContainer>
        )}
      </TableContainer>
    </S.TableWrapper>
  );
};

export default ClinicalEndpointsTableSwitched;
